import React, { useState, useMemo } from 'react'

import Icon from 'components_new/atoms/Icon'
import MenuItem from 'components_new/atoms/Menu/MenuItem'
import Table from 'components_new/atoms/Table'
import TableRow from 'components_new/atoms/Table/TableRow'
import TableCell from 'components_new/atoms/Table/TableCell'
import TableContainer from 'components_new/atoms/Table/TableContainer'
import TableBody from 'components_new/atoms/Table/TableBody'

import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'
import EditKpiVariableOrder from 'components_new/organisms/dialogs/EditKpiVariableOrder'

import AddVariableDialog from '../variable_dialogs/AddVariableDialog'
import EditVariableDialog from '../variable_dialogs/EditVariableDialog'

import Row from './Row'

import { TMetricCalculationBlock } from 'types/GlobalMetric'
import { ICustomEconomyMetric } from 'types/GlobalMetric'

import { KpiVariable } from 'types/GlobalKpiVariable'
import { useKpiVariableActions } from 'redux/actions/KpiVariables'
import { useTranslation } from 'language/translator'
import Link from 'components_new/atoms/Link'

interface VariablesSectionProps {
  allVariables: KpiVariable[]
  kpis: ICustomEconomyMetric[]
  level: number
  parentVariable: KpiVariable | null
  variables: KpiVariable[]
}

const variableInUseInBlock = (
  variableId: string,
  block: TMetricCalculationBlock
): boolean => {
  if (block.left_kpi_variable_id === variableId) {
    return true
  }

  if (block.right_kpi_variable_id === variableId) {
    return true
  }

  if (
    block.left_kpi_calculation_block &&
    variableInUseInBlock(variableId, block.left_kpi_calculation_block)
  ) {
    return true
  }

  if (block.right_kpi_calculation_block) {
    return variableInUseInBlock(variableId, block.right_kpi_calculation_block)
  }

  return false
}

const variableInUse = (variableId: string, kpis: ICustomEconomyMetric[]) => {
  const foundInUse = kpis.find((kpi) => {
    const block = kpi.custom_calculation_block

    return block ? variableInUseInBlock(variableId, block) : false
  })

  return !!foundInUse
}

const N_LEVELS = 3

const VariablesSection = (props: VariablesSectionProps) => {
  const { allVariables, kpis, level, parentVariable, variables } = props

  const { tryCreateKpiVariable, tryDeleteKpiVariable, tryUpdateKpiVariable } =
    useKpiVariableActions()

  const { t } = useTranslation()

  const [addVariable, setAddVariable] = useState<boolean>(false)
  const [editVariable, setEditVariable] = useState<KpiVariable | null>(null)
  const [deleteVariable, setDeleteVariable] = useState<KpiVariable | null>(null)
  const [editOrder, setEditOrder] = useState<{
    open: boolean
    id?: string
  }>({ open: false })

  const variablesInUse: { [id: string]: boolean } = useMemo(() => {
    const next: { [id: string]: boolean } = {}

    variables.forEach((variable) => {
      next[variable.id] = variableInUse(variable.id, kpis)
    })

    return next
  }, [kpis])

  const handleClose = () => {
    setEditOrder({ open: false })
  }

  const handleCreateVariable = (name: string) => {
    tryCreateKpiVariable({
      name,
      level,
      parent_kpi_variable_id: parentVariable?.id
    })
    setAddVariable(false)
  }

  const handleUpdateVariable = (name: string, callback: () => void) => {
    if (!editVariable) return

    tryUpdateKpiVariable(editVariable.id, { name }, (err) => {
      if (!err) {
        setEditVariable(null)
      }
      callback()
    })
  }

  const handleDeleteVariable = (callback: () => void) => {
    if (!deleteVariable) return

    tryDeleteKpiVariable(deleteVariable.id, (err) => {
      if (!err) {
        setDeleteVariable(null)
      }
      callback()
    })
  }

  return (
    <>
      <TableContainer
        sx={
          level === 0
            ? { border: '1px solid', borderColor: 'divider', borderRadius: 1 }
            : null
        }
      >
        <Table size="small">
          <TableBody>
            {variables.map((variable) => {
              return (
                <Row
                  inUse={variablesInUse[variable.id]}
                  kpis={kpis}
                  level={variable.level}
                  onEdit={() => setEditVariable(variable)}
                  onEditOrder={(id: string) => setEditOrder({ open: true, id })}
                  onDelete={() => setDeleteVariable(variable)}
                  variable={variable}
                  allVariables={allVariables}
                  key={variable.id}
                />
              )
            })}
            {level < N_LEVELS ? (
              <TableRow>
                <TableCell colSpan={3} sx={{ p: 0 }}>
                  <MenuItem
                    component="button"
                    onClick={() => setAddVariable(true)}
                    sx={{
                      height: 48,
                      width: '100%',
                      fontWeight: 500,
                      gap: 0.5,
                      pl: `calc(16px + 30px + 8px + ${level * 4 * 8}px)`
                    }}
                  >
                    <Icon fontSize="small" name="AddCircleOutlineOutlined" />
                    {t('global', 'add')}
                  </MenuItem>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      {level === 0 ? (
        <Link onClick={() => setEditOrder({ open: true })} variant="body2">
          <Icon
            color="inherit"
            fontSize="inherit"
            name={'LowPriorityOutlined'}
            sx={{
              mr: '0.25em'
            }}
          />
          {t('global', 'edit_order')}
        </Link>
      ) : null}

      {/* ----- Dialogs ----- */}
      <AddVariableDialog
        open={addVariable}
        onClose={() => setAddVariable(false)}
        onSave={handleCreateVariable}
        variables={allVariables}
      />
      {editVariable ? (
        <EditVariableDialog
          name={editVariable.name}
          open={Boolean(editVariable)}
          onClose={() => setEditVariable(null)}
          onSave={handleUpdateVariable}
          variableId={editVariable.id}
          variables={allVariables}
        />
      ) : null}
      {deleteVariable ? (
        <DeleteDialog
          open={Boolean(deleteVariable)}
          onClose={() => setDeleteVariable(null)}
          onDelete={handleDeleteVariable}
          title={deleteVariable.name}
          type={t('accounting', 'account_level', true)}
        />
      ) : null}
      <EditKpiVariableOrder
        id={editOrder.id}
        open={editOrder.open}
        onClose={handleClose}
        variables={allVariables}
      />
    </>
  )
}

export default VariablesSection
